import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    AlertTriangle,
    ArrowLeft,
    Check,
    ClipboardCheck,
    Info,
    ListChecks,
    MessageSquarePlus,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { formatPhone } from '../../../common/formatters';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import { useAppBarSubtitle, useAppBarTitle } from '../../../component/layout/AppBarActions';
import { KitBanner } from '../../../component/common/MobileKitExtras';
import useIsMobileShell from '../../../hooks/useIsMobileShell';
import { MobileFillView, renderPhotoBase64, useChecklistName } from './MobileChecklistFiller';
import SaveProgressDialog from './SaveProgressDialog';

// ============================================================================
// PREENCHIMENTO AVULSO DE CHECKLIST (`/checklist/:id/fill`) — redesign visual.
//
// Não há protótipo dedicado para esta página: ela reaproveita 1:1 o
// componente de preenchimento `check-group`/`check-item`/`check-answers`
// desenhado no modal "preencher checklist" de `prototipo-ordem-servico.html`
// (cabeçalho de grupo em gradiente da marca, linha por item com nome + botões
// de resposta OK/NOK/Manutenção em pílula, chip preenchido no tom da resposta
// escolhida — mesma paleta de `StatusChip domain="checklist"`, mesma anatomia
// de `AnswerToggleButton` de `GroupForm.js`), e o shell de página inteira
// (`form-hero` com voltar + título + anel de progresso, card em duas colunas
// com resumo sticky) já usado por `GroupForm.js`/`View.js` desta pasta.
//
// Comportamento preservado do `Filler.js` anterior (só a casca visual mudou):
//  - `GET /orderservices/{service_order_id}` (cabeçalho cliente/veículo; resposta
//    `{ order, tasks }` — ver o desempacotamento em `fetchServiceOrder`) e
//    `GET /checklist/{id}/view` (grupos/itens/respostas), em paralelo;
//  - `service_order_id` (ou `order_id`) vem por query string; sem ele o
//    cabeçalho não carrega e o "Salvar" recusa com "Dados incompletos";
//  - resposta por item (não dá para "desmarcar") + observação opcional;
//  - `POST /checklist/fill` com `{ service_order_id, checklist_id,
//    evaluations: [{ item_id, answer_id, observation }] }` só com os itens
//    respondidos; exige pelo menos um; sucesso volta para
//    `/serviceorders/{id}/view` após 1,5s;
//  - opções de resposta: as do próprio grupo, senão as de qualquer outro
//    grupo, senão OK/NOK/Manutenção (fallback já existente).
// ============================================================================

// Classifica um `Feedback` em ok/nok/manut pela mesma heurística de nome de
// `View.js`/`GroupForm.js` (grupos antigos usavam "Atenção" em vez de "NOK").
function classifyFeedback(name) {
    const upper = String(name || '').toUpperCase();
    if (upper === 'OK') return 'ok';
    if (upper === 'NOK' || upper.includes('ATEN')) return 'nok';
    if (upper.includes('MANUTEN')) return 'manut';
    return 'other';
}

function answerTone(theme, kind) {
    switch (kind) {
        case 'ok':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'nok':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'manut':
            return { bg: theme.palette.warning.bg, fg: theme.palette.warning.main };
        default:
            return { bg: theme.palette.info.bg, fg: theme.palette.primary.main };
    }
}

const DEFAULT_FEEDBACK_OPTIONS = [
    { feedback_id: 'ok', feedback_name: 'OK', feedback_order: 1 },
    { feedback_id: 'nok', feedback_name: 'NOK', feedback_order: 2 },
    { feedback_id: 'manutencao', feedback_name: 'Manutenção', feedback_order: 3 },
];

const byFeedbackOrder = (a, b) => (a.feedback_order || 0) - (b.feedback_order || 0);

// Opções de resposta de um grupo: as do próprio grupo; se ele não tem,
// herda as do primeiro grupo que tiver; se nenhum tem, usa o padrão.
function resolveFeedbackOptions(group, groups) {
    const own = Array.isArray(group.group_feedback)
        ? group.group_feedback.filter((f) => f.active !== false).sort(byFeedbackOrder)
        : [];
    if (own.length > 0) return own;
    for (const other of groups) {
        const fromOther = Array.isArray(other.group_feedback)
            ? other.group_feedback.filter((f) => f.active !== false).sort(byFeedbackOrder)
            : [];
        if (fromOther.length > 0) return fromOther;
    }
    return DEFAULT_FEEDBACK_OPTIONS;
}

function resolveInspectionItems(group) {
    return Array.isArray(group.group_inspection_items)
        ? group.group_inspection_items
            .filter((i) => i.active !== false)
            .sort((a, b) => (a.insp_item_order || 0) - (b.insp_item_order || 0))
        : [];
}

const RING_R = 23;
const RING_CIRC = 2 * Math.PI * RING_R;

// `.check-answers button` — pílula de resposta. Selecionada ganha o tom da
// resposta (bg + borda + texto); não selecionada é neutra com borda `divider`.
function AnswerButton({ kind, label, selected, onClick }) {
    const theme = useTheme();
    const { bg, fg } = answerTone(theme, kind);
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                fontFamily: 'inherit',
                border: '1.5px solid',
                borderColor: selected ? fg : 'divider',
                backgroundColor: selected ? bg : 'background.paper',
                color: selected ? fg : 'text.secondary',
                borderRadius: '20px',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                padding: '6px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'background-color .12s ease, border-color .12s ease, color .12s ease',
                '&:hover': { borderColor: fg, color: fg },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
        >
            {label}
        </Box>
    );
}

// `.ro-field` — label uppercase micro + valor (mesmo padrão de `View.js`).
function RoField({ label, value, muted }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

// Botão (ícone) que abre/fecha a observação opcional do item. Mantém a linha
// compacta como no protótipo; fica destacado quando já existe texto.
function ObservationToggle({ open, hasText, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            title={open ? 'Ocultar observação' : 'Adicionar observação'}
            aria-label={open ? 'Ocultar observação' : 'Adicionar observação'}
            aria-expanded={open}
            sx={(t) => ({
                fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, flexShrink: 0,
                borderRadius: '8px', cursor: 'pointer', p: 0,
                border: '1px solid', borderColor: open || hasText ? 'primary.main' : 'divider',
                backgroundColor: open || hasText ? 'info.bg' : 'background.paper',
                color: open || hasText ? 'primary.main' : 'text.disabled',
                transition: 'background-color .12s ease, border-color .12s ease, color .12s ease',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
            })}
        >
            <MessageSquarePlus size={14} strokeWidth={2.2} />
        </Box>
    );
}

// Linha `.check-item`: nome + botões de resposta + observação opcional.
function CheckItemRow({ item, isLast, feedbackOptions, evaluation, onAnswer, onObservation }) {
    const [obsOpen, setObsOpen] = useState(Boolean(evaluation.observation));

    return (
        <Box sx={{ p: '12px 16px', borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, fontWeight: 600 }}>
                    {item.insp_item_name}
                </Typography>
                <Box role="group" aria-label={`Resposta para ${item.insp_item_name}`} sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flexShrink: 0 }}>
                    {feedbackOptions.map((feedback) => (
                        <AnswerButton
                            key={feedback.feedback_id}
                            kind={classifyFeedback(feedback.feedback_name)}
                            label={feedback.feedback_name}
                            selected={evaluation.answer_id === feedback.feedback_id}
                            onClick={() => {
                                // Mesma regra de antes: reclicar na resposta atual não desmarca.
                                if (evaluation.answer_id !== feedback.feedback_id) {
                                    onAnswer(item.insp_item_id, feedback.feedback_id);
                                }
                            }}
                        />
                    ))}
                    <ObservationToggle
                        open={obsOpen}
                        hasText={Boolean(evaluation.observation)}
                        onClick={() => setObsOpen((prev) => !prev)}
                    />
                </Box>
            </Box>
            {obsOpen && (
                <Box sx={{ mt: '12px' }}>
                    <TextField
                        fullWidth
                        autoFocus={!evaluation.observation}
                        label="Observação"
                        placeholder="Adicione uma observação sobre este item (opcional)"
                        value={evaluation.observation || ''}
                        onChange={(e) => onObservation(item.insp_item_id, e.target.value)}
                        variant="outlined"
                        multiline
                        rows={2}
                        sx={standaloneFieldSx}
                    />
                </Box>
            )}
        </Box>
    );
}

// `.check-group` — cabeçalho em gradiente da marca + linhas `.check-item`.
function CheckGroup({ group, items, feedbackOptions, evaluations, onAnswer, onObservation }) {
    const theme = useTheme();
    const answeredCount = items.filter((it) => evaluations[it.insp_item_id]?.answer_id).length;

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', overflow: 'hidden', mb: '14px', backgroundColor: 'background.paper', boxShadow: theme.customShadows.sm }}>
            <Box
                sx={{
                    p: '13px 16px', background: theme.palette.gradient.brand, color: '#fff', fontSize: 13, fontWeight: 800,
                    display: 'flex', alignItems: 'center', gap: '8px',
                }}
            >
                <ClipboardCheck size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {group.group_name}
                </Box>
                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'rgba(255,255,255,.2)', px: '9px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {answeredCount}/{items.length}
                </Box>
            </Box>

            {group.group_description && (
                <Box sx={{ p: '9px 16px', fontSize: 11.5, color: 'text.secondary', backgroundColor: 'background.subtle', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {group.group_description}
                </Box>
            )}

            {items.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled', p: '22px 16px', textAlign: 'center' }}>
                    Nenhum item de inspeção cadastrado neste grupo
                </Typography>
            ) : items.map((item, idx) => (
                <CheckItemRow
                    key={item.insp_item_id}
                    item={item}
                    isLast={idx === items.length - 1}
                    feedbackOptions={feedbackOptions}
                    evaluation={evaluations[item.insp_item_id] || {}}
                    onAnswer={onAnswer}
                    onObservation={onObservation}
                />
            ))}
        </Box>
    );
}

// Skeleton do carregamento inicial (mesmo grid 1fr + 340px da página pronta).
const FillerSkeleton = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
        <Stack spacing={1.75}>
            <Skeleton variant="rounded" height={104} sx={{ borderRadius: '14px' }} />
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: '14px' }} />
            <Skeleton variant="rounded" height={220} sx={{ borderRadius: '14px' }} />
        </Stack>
        <Skeleton variant="rounded" height={240} sx={{ borderRadius: '18px' }} />
    </Box>
);

export default function ChecklistFiller() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id: checklistId } = useParams();
    const [searchParams] = useSearchParams();
    const serviceOrderId = searchParams.get('service_order_id') || searchParams.get('order_id');
    const isMobileShell = useIsMobileShell();

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [saveProgress, setSaveProgress] = useState(0);

    // Service order data
    const [serviceOrder, setServiceOrder] = useState(null);

    // Checklist data
    const [groups, setGroups] = useState([]);
    const [checklistLoadFailed, setChecklistLoadFailed] = useState(false);

    // Form state - evaluations: { item_id: { answer_id: string, observation: string } }
    const [evaluations, setEvaluations] = useState({});

    // Shell mobile: título = nome do template, subtítulo = número da OS (preencher.html).
    const mobileChecklistName = useChecklistName(isMobileShell ? checklistId : null);
    useAppBarTitle(isMobileShell ? (mobileChecklistName || 'Preencher checklist') : '');
    const mobileOrderNumber = serviceOrder ? (serviceOrder.number || serviceOrder.id) : '';
    useAppBarSubtitle(isMobileShell && mobileOrderNumber ? `OS #${mobileOrderNumber}` : '');

    const handleShowErrorSnackbar = useCallback((errorMessage) => {
        setOpenSnackbar(true);
        setMessage(errorMessage);
        setSeverity('error');
    }, []);

    const handleShowSuccessSnackbar = useCallback((successMessage) => {
        setOpenSnackbar(true);
        setMessage(successMessage);
        setSeverity('success');
    }, []);

    // Fetch service order data
    const fetchServiceOrder = async () => {
        if (!serviceOrderId) {
            handleShowErrorSnackbar('ID da ordem de serviço não fornecido');
            return;
        }

        try {
            const response = await httpClient.get(`/orderservices/${serviceOrderId}`);

            if (response.status === 200 && response.data) {
                // `GET /orderservices/{id}` devolve `{ order, tasks }` (mesmo
                // desempacotamento de `service_order/View.js`) — a versão anterior
                // lia os campos direto de `response.data` e o cabeçalho
                // cliente/veículo nunca preenchia.
                setServiceOrder(response.data.order || response.data);
            } else {
                handleShowErrorSnackbar('Erro ao carregar dados da ordem de serviço');
            }
        } catch (error) {
            console.error('Error fetching service order:', error);
            handleShowErrorSnackbar('Erro ao carregar dados da ordem de serviço');
        }
    };

    // Fetch checklist data
    const fetchChecklist = async () => {
        if (!checklistId) {
            handleShowErrorSnackbar('ID do checklist não fornecido');
            return;
        }

        try {
            const response = await httpClient.get(`/checklist/${checklistId}/view`);

            if (response.status === 200 && response.data) {
                setGroups(response.data);
            } else {
                setChecklistLoadFailed(true);
                handleShowErrorSnackbar('Erro ao carregar checklist');
            }
        } catch (error) {
            console.error('Error fetching checklist:', error);
            setChecklistLoadFailed(true);
            handleShowErrorSnackbar('Erro ao carregar dados do checklist');
        }
    };

    // Load initial data
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchServiceOrder(),
                fetchChecklist()
            ]);
            setLoading(false);
        };

        if (checklistId) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklistId, serviceOrderId]);

    // Handle answer change for an item
    const handleAnswerChange = (itemId, answerId) => {
        setEvaluations(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                answer_id: answerId
            }
        }));
    };

    // Handle observation change for an item
    const handleObservationChange = (itemId, observation) => {
        setEvaluations(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                observation: observation
            }
        }));
    };

    // Submit form
    const handleSubmit = async (photos = []) => {
        if (!serviceOrderId || !checklistId) {
            handleShowErrorSnackbar('Dados incompletos para envio');
            return;
        }

        // Build evaluations array
        const evaluationsArray = Object.entries(evaluations)
            .filter(([itemId, data]) => data.answer_id) // Only include items with answers
            .map(([itemId, data]) => ({
                item_id: itemId,
                answer_id: data.answer_id,
                observation: data.observation || ''
            }));

        if (evaluationsArray.length === 0) {
            handleShowErrorSnackbar('Por favor, preencha pelo menos um item do checklist');
            return;
        }

        const payload = {
            service_order_id: serviceOrderId,
            checklist_id: checklistId,
            evaluations: evaluationsArray
        };

        // Etapas ponderadas (mesmas de `ChecklistFillerModal.js`): envio do
        // checklist 0–45, fotos 45–55, envio do PDF 55–95; 100 ao concluir.
        // Nunca regride.
        const advance = (base, span, loaded, total) => {
            const fraction = total ? Math.min(loaded / total, 1) : 0;
            const next = Math.round(base + span * fraction);
            setSaveProgress((prev) => Math.max(prev, next));
        };

        try {
            setSaveProgress(0);
            setSubmitting(true);
            const response = await httpClient.post('/checklist/fill', payload, {
                onUploadProgress: (e) => advance(0, 40, e.loaded, e.total),
            });
            setSaveProgress((prev) => Math.max(prev, 45));

            if (response.status === 200 || response.status === 201) {
                // O POST do PDF é quem sobe o arquivo pro bucket. Falha aqui não
                // desfaz o checklist salvo — só avisa.
                let pdfFailed = false;
                try {
                    // Shell mobile: fotos com marcações de avaria (no desktop `photos` é vazio).
                    let rendered = 0;
                    const images = await Promise.all(photos.map(async (photo) => {
                        const image = await renderPhotoBase64(photo);
                        rendered += 1;
                        advance(45, 10, rendered, photos.length);
                        return image;
                    }));
                    setSaveProgress((prev) => Math.max(prev, 55));
                    await httpClient.post('/checklist/service-order/pdf', {
                        serviceOrderID: serviceOrderId,
                        checklistID: checklistId,
                        images,
                    }, {
                        onUploadProgress: (e) => advance(55, 40, e.loaded, e.total),
                    });
                } catch (pdfError) {
                    console.error('Error generating checklist PDF:', pdfError);
                    pdfFailed = true;
                }
                if (pdfFailed) {
                    handleShowSuccessSnackbar('Checklist salvo, mas não foi possível gerar o PDF.');
                } else {
                    handleShowSuccessSnackbar('Checklist preenchido com sucesso!');
                }
                setSaveProgress(100);
                // Mantém 100% e o botão bloqueado durante o atraso (sem duplo clique).
                await new Promise((resolve) => setTimeout(resolve, 1500));
                navigate(`/serviceorders/${serviceOrderId}/view`);
            } else {
                handleShowErrorSnackbar(response.data?.message || 'Erro ao salvar checklist');
            }
        } catch (error) {
            console.error('Error submitting checklist:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar checklist';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setSubmitting(false);
            setSaveProgress(0);
        }
    };

    const breadcrumbs = [
        { title: 'Veículos', link: '/vehicles' },
        { title: 'Checklists', link: '/vehicle_checklist' },
        { title: 'Preencher Checklist', link: '#' },
    ];

    // Estrutura de renderização (opções + itens por grupo) e resumo ao vivo:
    // itens respondidos / total e contagem por resposta (OK/NOK/Manutenção).
    const groupsView = useMemo(() => groups.map((group) => ({
        group,
        items: resolveInspectionItems(group),
        feedbackOptions: resolveFeedbackOptions(group, groups),
    })), [groups]);

    const summary = useMemo(() => {
        const tally = { ok: 0, nok: 0, manut: 0, other: 0 };
        let total = 0;
        let answered = 0;
        groupsView.forEach(({ items, feedbackOptions }) => {
            const kindByAnswerId = new Map(feedbackOptions.map((f) => [f.feedback_id, classifyFeedback(f.feedback_name)]));
            items.forEach((item) => {
                total += 1;
                const answerId = evaluations[item.insp_item_id]?.answer_id;
                if (answerId) {
                    answered += 1;
                    tally[kindByAnswerId.get(answerId) || 'other'] += 1;
                }
            });
        });
        return { total, answered, tally, pct: total > 0 ? Math.round((answered / total) * 100) : 0 };
    }, [groupsView, evaluations]);

    const ringOffset = RING_CIRC - (RING_CIRC * summary.pct / 100);

    const cardSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${theme.customRadius.card}px`,
        boxShadow: theme.customShadows.sm,
        backgroundColor: 'background.paper',
        p: '20px 22px',
        mb: '20px',
    };

    const customerName = serviceOrder
        ? (serviceOrder.customer_first_name || serviceOrder.customer_name
            ? `${serviceOrder.customer_first_name || ''} ${serviceOrder.customer_last_name || ''}`.trim() || serviceOrder.customer_name
            : '')
        : '';
    const customerPhone = serviceOrder
        ? formatPhone(serviceOrder.customer_cellphone || serviceOrder.customer_phone || '')
        : '';
    const vehicleText = serviceOrder?.vehicle_name
        ? `${serviceOrder.vehicle_name}${serviceOrder.vehicle_plate ? ` · ${serviceOrder.vehicle_plate}` : ''}`
        : '';

    const tallyRows = [
        { key: 'ok', label: 'OK', value: summary.tally.ok },
        { key: 'nok', label: 'NOK', value: summary.tally.nok },
        { key: 'manut', label: 'Manutenção', value: summary.tally.manut },
    ];
    if (summary.tally.other > 0) tallyRows.push({ key: 'other', label: 'Outras', value: summary.tally.other });

    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget="Preencher"
                links={breadcrumbs}
                message={message}
                severity={severity}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
            >
                <MobileFillView
                    loading={loading}
                    serviceOrder={serviceOrder}
                    groups={groups}
                    evaluations={evaluations}
                    onAnswer={handleAnswerChange}
                    onObservation={handleObservationChange}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                    saveProgress={saveProgress}
                    emptyText={checklistLoadFailed ? 'Não foi possível carregar o checklist' : 'Nenhum grupo encontrado neste checklist'}
                    warning={!serviceOrder && !serviceOrderId && !loading ? (
                        <KitBanner tone="warning" title="Nenhuma ordem de serviço informada.">
                            Abra este checklist a partir de uma ordem de serviço para poder salvar o preenchimento.
                        </KitBanner>
                    ) : null}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Preencher Checklist"
            titlePageTarget="Preencher"
            links={breadcrumbs}
            message={message}
            severity={severity}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* form-hero — voltar + título + anel de progresso (itens respondidos). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            title="Voltar"
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography component="h1" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                                Preencher checklist
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                Marque a resposta de cada item de inspeção do veículo.
                            </Typography>
                        </Box>
                    </Box>
                    {!loading && summary.total > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                            <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                                <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                    <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                    <circle
                                        cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" strokeLinecap="round"
                                        stroke={theme.palette.primary.main}
                                        strokeDasharray={RING_CIRC}
                                        strokeDashoffset={ringOffset}
                                        style={{ transition: 'stroke-dashoffset .35s ease' }}
                                    />
                                </svg>
                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                    {summary.pct}%
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Progresso</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                    {summary.answered} de {summary.total} {summary.total === 1 ? 'item respondido' : 'itens respondidos'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <FillerSkeleton />
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                        <Box>
                            {/* CARD — Ordem de serviço (cabeçalho cliente/veículo). */}
                            {serviceOrder ? (
                                <Box sx={cardSx}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Ordem de serviço</Typography>
                                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', lineHeight: 1.5 }}>
                                            O checklist será vinculado a esta ordem ao salvar.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px 18px' }}>
                                        <RoField label="Número da ordem" value={serviceOrder.number || serviceOrder.id} />
                                        <RoField label="Cliente" value={customerName} muted={!customerName} />
                                        <RoField label="Telefone/Celular" value={customerPhone} muted={!customerPhone} />
                                        {serviceOrder.vehicle_name && <RoField label="Veículo" value={vehicleText} />}
                                    </Box>
                                </Box>
                            ) : !serviceOrderId && (
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', mb: '20px', p: '12px 14px',
                                    borderRadius: '12px', backgroundColor: 'warning.bg',
                                }}>
                                    <AlertTriangle size={16} strokeWidth={2} color={theme.palette.warning.main} style={{ marginTop: 1, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12, color: 'text.primary', lineHeight: 1.5 }}>
                                        <Box component="b" sx={{ fontWeight: 700 }}>Nenhuma ordem de serviço informada.</Box>{' '}
                                        Abra este checklist a partir de uma ordem de serviço para poder salvar o preenchimento.
                                    </Typography>
                                </Box>
                            )}

                            {/* Grupos de inspeção e itens. */}
                            {groupsView.length === 0 ? (
                                <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '48px 20px', color: 'text.disabled', textAlign: 'center', gap: '10px' }}>
                                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {checklistLoadFailed ? <AlertTriangle size={24} strokeWidth={1.6} /> : <Info size={24} strokeWidth={1.6} />}
                                    </Box>
                                    <Typography sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 700 }}>
                                        {checklistLoadFailed ? 'Não foi possível carregar o checklist' : 'Nenhum grupo encontrado neste checklist'}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12.5px', maxWidth: 340 }}>
                                        {checklistLoadFailed
                                            ? 'Verifique o link ou tente novamente em instantes.'
                                            : 'Anexe grupos de inspeção ao template para poder preenchê-lo.'}
                                    </Typography>
                                </Box>
                            ) : groupsView.map(({ group, items, feedbackOptions }) => (
                                <CheckGroup
                                    key={group.group_id}
                                    group={group}
                                    items={items}
                                    feedbackOptions={feedbackOptions}
                                    evaluations={evaluations}
                                    onAnswer={handleAnswerChange}
                                    onObservation={handleObservationChange}
                                />
                            ))}
                        </Box>

                        {/* RESUMO STICKY — progresso ao vivo + ações. */}
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1.25 }}>
                                <ListChecks size={13} strokeWidth={2} /> Resumo
                            </Typography>
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.hero - 2}px`,
                                backgroundColor: 'background.paper', boxShadow: theme.customShadows.md, overflow: 'hidden',
                            }}>
                                <Box sx={{ p: '16px 18px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: '8px' }}>
                                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>Itens respondidos</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{summary.answered} / {summary.total}</Typography>
                                    </Box>
                                    <Box sx={{ height: 8, borderRadius: '8px', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: '14px' }}>
                                        <Box sx={{ height: '100%', width: `${summary.pct}%`, backgroundImage: theme.palette.gradient.brand, borderRadius: '8px', transition: 'width .35s ease' }} />
                                    </Box>
                                    {tallyRows.map((row) => {
                                        const { bg, fg } = answerTone(theme, row.key);
                                        return (
                                            <Box
                                                key={row.key}
                                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', py: '7px', fontSize: '12.5px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
                                            >
                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '4px', borderRadius: '20px', fontSize: 11, fontWeight: 700, lineHeight: 1, backgroundColor: bg, color: fg }}>
                                                    {row.label}
                                                </Box>
                                                <Box component="b" sx={{ color: 'text.primary' }}>{row.value}</Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Box sx={{ p: '14px 18px 18px', borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <Check size={15} strokeWidth={2.4} />}
                                        onClick={() => handleSubmit()}
                                        disabled={submitting || Object.keys(evaluations).length === 0}
                                    >
                                        {submitting ? 'Salvando...' : 'Salvar checklist'}
                                    </Button>
                                    <Button variant="outlined" fullWidth onClick={() => navigate(-1)}>
                                        Cancelar
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
            <SaveProgressDialog open={submitting} progress={saveProgress} />
        </PageScaffold>
    );
}
